/*
 *  (c) 2015 - 2023 ENISCO by FORCAM GmbH
 */

import {Collapse, List, ListItemButton, ListItemIcon, ListItemText, Typography} from '@mui/material'
import React, {Fragment, useState} from 'react'
import {clone, TranslatedText, translateText, useLanguage} from '@enisco/mcc-react-lib'
import {ExpandLess, ExpandMore} from '@mui/icons-material'
import {DefaultGroupIcon} from '../../../../icon/DefaultGroupIcon'
import {DefaultAppIcon} from '../../../../icon/DefaultAppIcon'
import {App, Module} from '../../../../model/apps'
import {useNavigate} from 'react-router-dom'
import {pathToApp} from '../../../../util/navigation'

interface Props {
  handleClose: () => void
  module: Module
}

const ModuleViewer: React.FC<Props> = ({handleClose, module}) => {
  const [open, setOpen] = useState(false)
  const language = useLanguage()
  const navigate = useNavigate()

  const registeredApps = clone(module.apps || [])
  const filteredApps = registeredApps.filter((app) => app.hasAccess)
  filteredApps.sort((a, b) =>
    (translateText(language, a.description) || '').localeCompare(
      translateText(language, b.description) || ''
    )
  )

  const handleClick = () => {
    setOpen(!open)
  }

  const openApp = (app: App) => {
    handleClose()
    navigate({pathname: pathToApp(app.module, app.app)})
  }

  return (
    <Fragment>
      <ListItemButton onClick={() => handleClick()}>
        <ListItemIcon>
          {module.icon ? (
            <img width={'32px'} height={'32px'} src={module.icon} alt='' />
          ) : (
            <DefaultGroupIcon sx={{width: '32px', height: '32px'}} />
          )}
        </ListItemIcon>
        <ListItemText primary={<TranslatedText text={module.description} />} />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>

      <Collapse in={open} timeout='auto' unmountOnExit>
        <List component='div' disablePadding>
          {filteredApps
            .filter((app) => !app.hidden)
            .map((app) => (
              <ListItemButton
                sx={{paddingLeft: 4}}
                onClick={() => openApp(app)}
                key={app.module + '_' + app.app}
              >
                <ListItemIcon>
                  {app.icon ? (
                    <img src={app.icon} width={'32px'} height={'32px'} alt='' />
                  ) : (
                    <DefaultAppIcon
                      sx={{width: '32px', height: '32px'}}
                      color={'primary'}
                    />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant='body1'>
                      <TranslatedText text={app.description} />
                    </Typography>
                  }
                />
              </ListItemButton>
            ))}
        </List>
      </Collapse>
    </Fragment>
  )
}

export default ModuleViewer
